import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { dateOnly, dayRange, todayISO, timeInTz, minutesInTz } from '../../lib/format';
import { PageRoot, PageHeader, KpiGrid, KpiCard } from '../ui/Page';
import { UnderlineTabs, UnderlineTabStrip, UnderlineTabPanel } from '../ui/Tabs';
import { TODAY_TABS, useUrlTab } from '../ui/pageTabs';
import { StatusBadge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState, StaleNotice } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { SectionCard } from '../ui/Detail';
import { VISIT_STATUS, JOB_STATUS, display } from '../status';
import { money, daysOverdue, invoiceBalanceCents } from '../money';
import { IconToday, IconArrowRight } from '../icons';
import { WithOrgClock } from '../ui/WithOrgClock';
import type { JobStatusCounts } from '@servwell/shared';
import { GettingStartedCard } from '../onboarding/GettingStartedCard';
import { groupByHour } from '../today/dayHours';
import { attentionFeed, attentionPreview, type AttentionItem } from '../today/attention';

type Visit = {
  visit: { id: string; status: string; scheduledStartAt: string | Date };
  job: { id: string; jobNumber: number; title: string };
  customer: { id: string; displayName: string };
  assignees: { userId: string; name: string }[];
};
type Invoice = {
  id: string;
  invoiceNumber: number | null;
  customerId: string;
  /** Joined by the server — see the note on listJobs. */
  customerName: string | null;
  status: string;
  dueDate: string | null;
  totalCents: number;
  lateFeeCents: number;
  amountPaidCents: number;
};

/** How many attention rows the Overview card shows before deferring to the tab. */
const PREVIEW_CAP = 4;
/** The overdue page the Attention tab asks for. Its TOTAL is what the page states. */
const OVERDUE_PAGE = 50;

/**
 * The office landing (117): what is happening today, and what needs a decision.
 *
 * ⚠️ THE OFFICE landing, and only the office — SPEC **D8a**. `/today` is `OFFICE_ONLY` in
 * the router and the nav, and a tech's day lives at `/tech`, which is a complete field app
 * with four offline subsystems. D8's "no second today surface" read literally would have
 * meant retiring `/tech` INTO this page and rebuilding those subsystems here; the amendment
 * accepts the inversion instead, and this page never asks who is reading it.
 */
export function TodayPage() {
  return (
    <WithOrgClock what="today's work" icon={IconToday} title="Today">
      {(tz) => <TodayBoard tz={tz} />}
    </WithOrgClock>
  );
}

function TodayBoard({ tz }: { tz: string }) {
  const trpc = useTRPC();
  const [tab, setTab] = useUrlTab(TODAY_TABS);
  // Once per render, not once per invoice row.
  const today = todayISO(tz);
  const visits = useQuery(trpc.visit.board.queryOptions(dayRange(today, tz)));

  /**
   * 🔴 `overdue: true` ON THE SERVER (117). This used to ask for 50 OPEN invoices and filter
   * them here for the overdue ones — so the list was "the overdue invoices among the newest
   * page", and its count was that subset's length. The oldest overdue invoice, the one that
   * most needs chasing, is exactly the one that falls off the end of such a page.
   *
   * The `total` that comes back is the shop's overdue count, computed on the same predicate
   * as the KPI below, which is why the two agree by construction rather than by luck.
   */
  const overdue = useQuery(
    trpc.invoice.list.queryOptions({
      open: true,
      overdue: true,
      sort: 'due',
      dir: 'asc',
      limit: OVERDUE_PAGE,
      offset: 0,
    }),
  );
  /** The rail. A SERVER aggregate — see `jobStatusCounts` for why it cannot be a count here. */
  const rail = useQuery(trpc.job.statusCounts.queryOptions());

  if (visits.isPending || overdue.isPending) return <PageSkeleton />;
  if (visits.isError || overdue.isError) {
    return (
      <PageRoot>
        <PageHeader title="Today" />
        <ErrorState
          icon={IconToday}
          what="today's work"
          onRetry={() => {
            void visits.refetch();
            void overdue.refetch();
          }}
        />
      </PageRoot>
    );
  }

  const todaysVisits = visits.data as Visit[];
  const overdueRows = overdue.data.rows as Invoice[];
  const unassigned = todaysVisits.filter((v) => v.assignees.length === 0);
  const doneToday = todaysVisits.filter((v) => v.visit.status === 'completed').length;

  const feed = attentionFeed(
    unassigned.map((v) => ({
      visitId: v.visit.id,
      customerName: v.customer.displayName,
      time: timeInTz(v.visit.scheduledStartAt, tz),
    })),
    overdueRows.map((i) => ({
      invoiceId: i.id,
      customerName: i.customerName,
      balance: money(invoiceBalanceCents(i)),
      daysLate: daysOverdue(i.dueDate, today),
    })),
    overdue.data.total,
  );

  return (
    <PageRoot>
      {/*
        The shop's date, not the reader's. `new Date().toDateString()` printed the BROWSER's
        day directly under the word "Today" — so an owner checking in from another zone read
        one date in the header while every number below it was counted for another.
      */}
      <PageHeader
        title="Today"
        sub={dateOnly(today, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      />

      <GettingStartedCard />

      {/*
        The four tiles (A1, approved). Three of them are things an owner acts on before 9am;
        "Outstanding but not yet due" was the fourth and now stays on `/money`, which owns
        that surface. Unassigned took its place because it is the one number on this page
        with a fix attached.
      */}
      <KpiGrid>
        <KpiCard label="Visits today" value={todaysVisits.length} />
        <KpiCard
          label="Done"
          value={`${doneToday}/${todaysVisits.length}`}
          tone="success"
        />
        <KpiCard
          label="Unassigned"
          value={unassigned.length}
          tone={unassigned.length > 0 ? 'warn' : undefined}
        />
        <KpiCard
          label="Overdue"
          value={overdue.data.total}
          tone={overdue.data.total > 0 ? 'danger' : undefined}
        />
      </KpiGrid>

      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip
          tabs={[
            { key: 'overview', label: 'Overview' },
            // The count is the FEED's total, the same number the card's footer names.
            { key: 'attention', label: `Attention (${feed.total})` },
          ]}
        />

        {/*
         * ⚠️ `keepMounted` on BOTH — 105's standing law. Attention's rows carry actions, and
         * an inline mutation error painted into an unmounted panel is an error nobody sees.
         * Overview holds the rail and the hour grid; remounting them re-reads a cache, but
         * the law is per-page consistency, not per-panel judgement.
         */}
        <UnderlineTabPanel value="overview" keepMounted>
          {/*
            The approved two-column Overview: the day on the left, what to DO about it on
            the right. One column below `lg`, where the right column would be a 130px
            gutter — and where the same content simply continues down the page, which is
            why the day comes FIRST there: on a phone the first thing a dispatcher wants
            is the next appointment, not a summary of the backlog.
          */}
          {/* `grid-cols-1` below `lg` (one `minmax(0, 1fr)` column): with no template the
              implicit column is `auto`, sized to the day's widest line, 593px in a 358px
              grid at 390, and the page scrolled sideways (MAP 227; Phase 2 QA, R2-I2). */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <DayByHour visits={todaysVisits} tz={tz} />
            <div className="space-y-4">
              <StatusRail
                data={rail.data}
                stale={rail.isError}
                onRetry={() => void rail.refetch()}
              />
              <AttentionCard feed={feed} onViewAll={() => setTab('attention')} />
            </div>
          </div>
        </UnderlineTabPanel>

        <UnderlineTabPanel value="attention" keepMounted>
          <SectionCard
            title="Needs your attention"
            right={
              feed.truncated ? (
                <span className="text-xs text-muted-foreground">
                  Showing {feed.items.length} of {feed.total}
                </span>
              ) : null
            }
          >
            {feed.items.length === 0 ? (
              <EmptyState
                icon={IconToday}
                title="Nothing needs you"
                description="Every visit today has somebody on it, and no invoice is past its due date."
              />
            ) : (
              <ul className="divide-y divide-border text-sm">
                {feed.items.map((item) => (
                  <AttentionRow key={item.key} item={item} />
                ))}
              </ul>
            )}
          </SectionCard>
        </UnderlineTabPanel>
      </UnderlineTabs>
    </PageRoot>
  );
}

function AttentionRow({ item }: { item: AttentionItem }) {
  return (
    <li className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
      <span className="min-w-0">
        <span className="font-medium">{item.who}</span>{' '}
        <span className="text-muted-foreground">{item.what}</span>
      </span>
      <Link
        to={item.to}
        className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
      >
        {item.action} <IconArrowRight size={14} />
      </Link>
    </li>
  );
}

/**
 * Overview's capped view of the SAME feed.
 *
 * 🔴 The footer names `total`, never the slice's length — a "View all (4)" over four rows
 * tells a reader they are looking at everything. That is the 007 defect, and it is the one
 * thing this component exists to get right.
 */
function AttentionCard({
  feed,
  onViewAll,
}: {
  feed: ReturnType<typeof attentionFeed>;
  onViewAll: () => void;
}) {
  const preview = attentionPreview(feed, PREVIEW_CAP);
  if (preview.total === 0) return null;
  return (
    <SectionCard title="Needs your attention">
      <ul className="divide-y divide-border text-sm">
        {preview.items.map((item) => (
          <AttentionRow key={item.key} item={item} />
        ))}
      </ul>
      {preview.hidden > 0 ? (
        <button
          type="button"
          onClick={onViewAll}
          className="mt-2 text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
        >
          View all ({preview.total})
        </button>
      ) : null}
    </SectionCard>
  );
}

/**
 * Today's visits, grouped by the hour they start.
 *
 * ⭐ The empty hours are drawn. A flat list of five visits reads as a full day; the same
 * five under hour headings show three before ten and nothing after two, and an empty
 * afternoon is a thing an owner can sell. See `dayHours.ts` for why the span is the work
 * rather than an invented 8-to-6.
 */
function DayByHour({ visits, tz }: { visits: Visit[]; tz: string }) {
  const groups = groupByHour(visits, (v) =>
    Math.floor(minutesInTz(v.visit.scheduledStartAt, tz) / 60),
  );
  return (
    <SectionCard
      title="Today's visits"
      right={
        <Link
          to="/schedule"
          className="text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
        >
          Open schedule
        </Link>
      }
    >
      {groups.length === 0 ? (
        <EmptyState
          icon={IconToday}
          title="Nothing scheduled today"
          description="When you schedule a visit on a job, it shows up here."
        />
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.hour} className="flex gap-3">
              <span className="w-16 shrink-0 pt-1 font-mono text-xs text-muted-foreground tabular-nums">
                {group.label}
              </span>
              <div className="min-w-0 flex-1">
                {group.items.length === 0 ? (
                  // The empty hour, drawn rather than skipped. A rule, not a word: "nothing
                  // booked" repeated down a quiet afternoon is noise, and the gap is the
                  // information.
                  <div className="h-4 border-b border-dashed border-border" />
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {group.items.map((v) => {
                      const s = display(VISIT_STATUS, v.visit.status);
                      return (
                        <li
                          key={v.visit.id}
                          className="flex items-center gap-3 py-2 first:pt-0 last:pb-0"
                        >
                          <span className="w-14 shrink-0 font-mono text-xs text-muted-foreground">
                            {timeInTz(v.visit.scheduledStartAt, tz)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">
                              {v.customer.displayName}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              #{v.job.jobNumber} · {v.job.title} ·{' '}
                              {v.assignees.map((a) => a.name).join(', ') || 'Unassigned'}
                            </span>
                          </span>
                          <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/**
 * Open jobs by status.
 *
 * ⚠️ The caption names what is NOT counted, and it names it from the PAYLOAD. An unstated
 * denominator is how a percentage lies: "48 jobs" means nothing to a reader who does not
 * know whether the shop's closed work is in it. The excluded states ride back with the
 * counts so this line cannot drift from the predicate that made it true.
 */
function StatusRail({
  data,
  stale,
  onRetry,
}: {
  data: JobStatusCounts | undefined;
  stale: boolean;
  onRetry: () => void;
}) {
  if (!data) return null;
  const { counts, total, excluded } = data;
  return (
    <SectionCard
      title="Open work orders"
      right={
        <Link
          to="/work-orders"
          className="text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
        >
          Open work orders
        </Link>
      }
    >
      <StaleNotice show={stale} onRetry={onRetry} />
      {total === 0 ? (
        <p className="text-sm text-muted-foreground">No open work orders.</p>
      ) : (
        <ul className="space-y-2.5">
          {counts.map((c) => {
            const s = display(JOB_STATUS, c.status);
            /**
             * ⚠️ NULL AT ZERO, never 100% — 111's rule, and the reason is that a share of
             * nothing is not a share. `total === 0` is already handled above, so this
             * division cannot produce NaN; the guard is kept as the statement of why.
             */
            const pct = total > 0 ? Math.round((c.count / total) * 100) : null;
            return (
              <li key={c.status}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="truncate text-muted-foreground">{s.label}</span>
                  <span className="flex shrink-0 items-baseline gap-2">
                    <span className="font-heading font-bold tabular-nums">{c.count}</span>
                    <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
                      {pct === null ? '—' : `${pct}%`}
                    </span>
                  </span>
                </div>
                {/* The bar is a SECOND encoding of the number beside it, never the only
                    one — a reader who cannot separate the hues still has the count. */}
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2">
                  <div
                    className="h-full rounded-full bg-brand"
                    style={{ width: `${pct ?? 0}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        {total} open {total === 1 ? 'work order' : 'work orders'}.{' '}
        {excluded.map((e) => display(JOB_STATUS, e).label).join(' and ')} work orders are
        not counted.
      </p>
    </SectionCard>
  );
}
